import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { AppIcon } from '../ui/icon'
import { subjectIcon } from './assembly-icons'
import { useDocumentResolutions } from './assembly-api'
import { forceTone, outcomeTone, ruleShort } from './assembly-utils'

/**
 * The resolutions extracted from a PV (CONDO_AGM_MINUTES) document: title,
 * outcome, and a jump to the page they were read from, above the raw text.
 * Spec 2026-10-10-08 §10.
 */
export function DocumentResolutions({
    org,
    documentUid,
    onJumpToPage,
}: {
    org: string
    documentUid: string
    onJumpToPage: (page: number) => void
}) {
    const { t } = useTranslation('assemblies')
    const { data, isLoading } = useDocumentResolutions(org, documentUid, true)
    const rows = data?.data ?? []
    if (isLoading) return null
    return (
        <Card data-testid="document-resolutions">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('docs.title')}</CardTitle>
            </CardHeader>
            <CardContent>
                {rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="document-resolutions-empty">{t('docs.empty')}</p>
                ) : (
                    <ol className="divide-y text-sm" data-testid="document-resolutions-list">
                        {rows.map((d) => (
                            <li key={d.resolution_uid} className="flex flex-wrap items-center gap-3 py-2" data-testid={`document-resolution-${d.resolution_uid}`}>
                                <span className="w-6 shrink-0 text-right tabular-nums text-muted-foreground">{d.resolution_number ?? ''}</span>
                                <span className="min-w-0 flex-1">
                                    <Link
                                        to="/$org/assemblies/$uid"
                                        params={{ org, uid: d.minutes_uid }}
                                        search={{ resolution: d.resolution_uid }}
                                        className="block truncate font-medium underline-offset-2 hover:underline"
                                        data-testid={`document-resolution-link-${d.resolution_uid}`}
                                    >
                                        {d.title}
                                    </Link>
                                    <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                        {d.resolution_subject && (
                                            <span className="inline-flex items-center gap-1">
                                                <AppIcon path={subjectIcon(d.resolution_subject)} size="xs" tone="muted" />
                                                {t(`subject.${d.resolution_subject}`, { defaultValue: d.resolution_subject })}
                                            </span>
                                        )}
                                        {ruleShort(d.majority_rule) && <Badge variant="outline">{ruleShort(d.majority_rule)}</Badge>}
                                    </span>
                                </span>
                                <Badge variant={outcomeTone(d.outcome)} data-testid={`document-resolution-outcome-${d.resolution_uid}`}>
                                    {t(`outcome.${d.outcome}`)}
                                </Badge>
                                <Badge variant={forceTone(d.force)} className="hidden sm:inline-flex">{t(`decisions.force.${d.force}`)}</Badge>
                                {d.source_page ? (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => onJumpToPage(d.source_page!)}
                                        title={t('docs.jump')}
                                        data-testid={`document-resolution-page-${d.resolution_uid}`}
                                    >
                                        {t('docs.page', { page: d.source_page })}
                                    </Button>
                                ) : null}
                            </li>
                        ))}
                    </ol>
                )}
            </CardContent>
        </Card>
    )
}
